import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, LogOut, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ExitFormPage() {
  const [loading, setLoading] = useState(true);
  const [exitData, setExitData] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    reason_for_leaving: '',
    experience_rating: '',
    what_liked_most: '',
    what_could_improve: '',
    would_rejoin: '',
    suggestions: '',
    id_card_returned: false,
    laptop_returned: false,
    assets_returned: false,
    pending_dues_cleared: false,
    knowledge_transfer_done: false,
  });

  useEffect(() => {
    api.get('/api/exit-form/my').then(r => {
      setExitData(r.data);
      if (r.data.form) {
        setForm(prev => ({
          ...prev,
          reason_for_leaving: r.data.form.reason_for_leaving || '',
          experience_rating: r.data.form.experience_rating || '',
          what_liked_most: r.data.form.what_liked_most || '',
          what_could_improve: r.data.form.what_could_improve || '',
          would_rejoin: r.data.form.would_rejoin || '',
          suggestions: r.data.form.suggestions || '',
          id_card_returned: r.data.form.id_card_returned || false,
          laptop_returned: r.data.form.laptop_returned || false,
          assets_returned: r.data.form.assets_returned || false,
          pending_dues_cleared: r.data.form.pending_dues_cleared || false,
          knowledge_transfer_done: r.data.form.knowledge_transfer_done || false,
        }));
        if (r.data.form.status === 'submitted') setSubmitted(true);
      }
      setLoading(false);
    }).catch(() => { setLoading(false); });
  }, []);

  const handleSubmit = async () => {
    if (!form.reason_for_leaving.trim()) { toast.error('Please provide your reason for leaving'); return; }
    if (!form.experience_rating) { toast.error('Please rate your experience'); return; }
    setSaving(true);
    try {
      await api.post('/api/exit-form/submit', form);
      toast.success('Exit form submitted successfully');
      setSubmitted(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#0D2850]" /></div>;

  if (!exitData?.exit_initiated) {
    return (
      <div data-testid="exit-form-page">
        <Badge className="bg-[#0D2850] text-white text-[10px] mb-3 hover:bg-[#0D2850]">// EXIT FORM</Badge>
        <Card className="border-slate-200 max-w-lg mx-auto mt-8">
          <CardContent className="p-10 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-[#0D2850]">No Exit Pending</h2>
            <p className="text-sm text-slate-500 mt-2">Your account is active. No exit process has been initiated for you.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const emp = exitData.employee || {};

  if (submitted) {
    return (
      <div data-testid="exit-form-page">
        <Badge className="bg-[#0D2850] text-white text-[10px] mb-3 hover:bg-[#0D2850]">// EXIT FORM</Badge>
        <Card className="border-slate-200 max-w-lg mx-auto mt-8">
          <CardContent className="p-10 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-[#0D2850]">Exit Form Submitted</h2>
            <p className="text-sm text-slate-500 mt-2">Thank you for completing the exit formalities. Your HR team will review and process this.</p>
            <div className="bg-slate-50 rounded-lg p-3 mt-4 text-sm text-left space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">Exit Reason</span><span>{emp.exit_reason}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Last Working Date</span><span>{emp.last_working_date || 'TBD'}</span></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="exit-form-page">
      <Badge className="bg-[#0D2850] text-white text-[10px] mb-3 hover:bg-[#0D2850]">// EXIT FORM</Badge>
      <div className="flex items-center gap-2 mb-1">
        <LogOut className="w-5 h-5 text-red-500" />
        <h1 className="text-2xl font-semibold text-[#0D2850] tracking-tight">Exit Formalities</h1>
      </div>
      <p className="text-sm text-slate-500 mb-6">Please complete the following exit form before your last working day.</p>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm">
          <span className="font-medium text-amber-800">Exit initiated</span>
          <span className="text-amber-700 ml-1">
            — Reason: <b>{emp.exit_reason}</b>
            {emp.last_working_date && <>, Last Working Date: <b>{emp.last_working_date}</b></>}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Exit Interview */}
        <Card className="border-slate-200">
          <CardHeader className="py-3 px-4 pb-2">
            <CardTitle className="text-sm font-semibold">Exit Interview</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-4">
            <div>
              <Label className="text-xs">Reason for leaving <span className="text-red-500">*</span></Label>
              <Textarea value={form.reason_for_leaving} onChange={e => setForm({...form, reason_for_leaving: e.target.value})} placeholder="Please share your primary reason..." rows={3} data-testid="exit-reason-for-leaving" />
            </div>
            <div>
              <Label className="text-xs">How would you rate your overall experience? <span className="text-red-500">*</span></Label>
              <Select value={form.experience_rating} onValueChange={v => setForm({...form, experience_rating: v})}>
                <SelectTrigger data-testid="exit-experience-rating"><SelectValue placeholder="Select rating..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 - Excellent</SelectItem>
                  <SelectItem value="4">4 - Good</SelectItem>
                  <SelectItem value="3">3 - Average</SelectItem>
                  <SelectItem value="2">2 - Below Average</SelectItem>
                  <SelectItem value="1">1 - Poor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">What did you like most about working here?</Label>
              <Textarea value={form.what_liked_most} onChange={e => setForm({...form, what_liked_most: e.target.value})} placeholder="Team, culture, projects..." rows={2} data-testid="exit-what-liked" />
            </div>
            <div>
              <Label className="text-xs">What could be improved?</Label>
              <Textarea value={form.what_could_improve} onChange={e => setForm({...form, what_could_improve: e.target.value})} placeholder="Processes, management, benefits..." rows={2} data-testid="exit-what-improve" />
            </div>
            <div>
              <Label className="text-xs">Would you consider rejoining in the future?</Label>
              <Select value={form.would_rejoin} onValueChange={v => setForm({...form, would_rejoin: v})}>
                <SelectTrigger data-testid="exit-would-rejoin"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="Maybe">Maybe</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Any suggestions for the organization?</Label>
              <Textarea value={form.suggestions} onChange={e => setForm({...form, suggestions: e.target.value})} placeholder="Your feedback helps us improve..." rows={2} data-testid="exit-suggestions" />
            </div>
          </CardContent>
        </Card>

        {/* No-Dues Checklist */}
        <Card className="border-slate-200">
          <CardHeader className="py-3 px-4 pb-2">
            <CardTitle className="text-sm font-semibold">No-Dues Checklist</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-4">
            <p className="text-xs text-slate-500">Please confirm the following items have been returned or completed before your last working day.</p>
            {[
              { key: 'id_card_returned', label: 'ID Card Returned', desc: 'Employee ID card handed over to HR' },
              { key: 'laptop_returned', label: 'Laptop / Equipment Returned', desc: 'Company laptop and peripherals returned' },
              { key: 'assets_returned', label: 'Other Assets Returned', desc: 'Access cards, keys, uniforms, tools etc.' },
              { key: 'pending_dues_cleared', label: 'Pending Dues Cleared', desc: 'All financial dues and advances settled' },
              { key: 'knowledge_transfer_done', label: 'Knowledge Transfer Completed', desc: 'Work handover and documentation completed' },
            ].map(item => (
              <div key={item.key} className="flex items-start gap-3 p-3 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors">
                <Checkbox
                  checked={form[item.key]}
                  onCheckedChange={v => setForm({...form, [item.key]: v})}
                  data-testid={`exit-${item.key}`}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-[#0B1B33]">{item.label}</p>
                  <p className="text-xs text-slate-400">{item.desc}</p>
                </div>
              </div>
            ))}

            <div className="pt-4 border-t border-slate-100">
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="w-full bg-[#0D2850] hover:bg-[#0B2246]"
                data-testid="submit-exit-form-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Exit Form'}
              </Button>
              <p className="text-[10px] text-slate-400 mt-2 text-center">Once submitted, your HR team will review and process the exit.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
